import {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import './ReviewAndApprove.scss';
import ReviewAndApproveHeader from './review-and-approve-header/ReviewAndApproveHeader';
import ReviewAndApproveContent from './review-and-approve-content/ReviewAndApproveContent';
import ReviewLeaveWithoutApprovalModal from './review-leave-without-approval-modal/ReviewLeaveWithoutApprovalModal';
import {useReviewHeaderContext} from '../header/ReviewHeader';
import {t} from 'i18next';
import {
  IssueCode,
  IUpdateProtocolIntelligenceDetailsPayload,
} from '../../../../api/types/aiReview.type';
import {
  useApproveProtocolIntelligence,
  useUpdateProtocolIntelligenceDetails,
} from '../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../shared/constants';
import CancelEditProtocolIntelligenceModal from '../protocolIntelligence/editProtocolIntelligence/CancelEditProtocolIntelligenceModal';

const ReviewAndApprove = () => {
  const {
    handleApproveArtifacts,
    setReviewHeaderToast,
    protocolDetails,
    showEditProtocolIntelligence,
    fileName,
    setShowEditProtocolIntelligence,
    refetchProtocolData,
  } = useReviewHeaderContext();

  const protocolId = protocolDetails?.protocolId;

  const [
    showGenerateArtifactsApprovalModal,
    setShowGenerateArtifactsApprovalModal,
  ] = useState<boolean>(false);

  const [
    showCancelEditProtocolIntelligenceModal,
    setShowCancelEditProtocolIntelligenceModal,
  ] = useState(false);

  // States from ReviewAndApproveContent
  const [caseContext, setCaseContext] = useState('');
  const [relevanceCriteria, setRelevanceCriteria] = useState('');
  const [issueCodeData, setIssueCodeData] = useState<IssueCode[]>([]);

  const {mutate: approveProtocolIntelligence} =
    useApproveProtocolIntelligence();

  const {mutate: updateProtocolIntelligenceDetails} =
    useUpdateProtocolIntelligenceDetails();

  const showProtocolDetailsPage = () => {
    setShowCancelEditProtocolIntelligenceModal(false);
    setShowEditProtocolIntelligence(false);
  };

  const handleShowGenerateArtifactsApprovalModalClose = () => {
    setShowGenerateArtifactsApprovalModal(false);
  };

  const handleApproveCancel = () => {
    setShowGenerateArtifactsApprovalModal(true);
  };

  const handleEditCancelClick = () => {
    setShowCancelEditProtocolIntelligenceModal(true);
  };

  const handleEditSaveClick = () => {
    if (!protocolId) return;
    const payload: IUpdateProtocolIntelligenceDetailsPayload = {
      protocolId,
      isActive: protocolDetails?.isActive,
      caseContext,
      relevanceCriteria,
      issueCodes: issueCodeData,
    };
    updateProtocolIntelligenceDetails(payload, {
      onSuccess: () => {
        showProtocolDetailsPage();
        setReviewHeaderToast({
          isOpen: true,
          message: t('ai.review.protocolIntelligence.isProtocolUpdateSuccess'),
          status: ToastType.Success,
        });
        refetchProtocolData();
      },
      onError: (error) => {
        setReviewHeaderToast({
          isOpen: true,
          message:
            error?.response?.data?.detail ??
            t('ai.review.protocolIntelligence.isProtocolUpdateFailure'),
          status: ToastType.Error,
        });
      },
    });
  };

  const handleApprove = () => {
    if (!protocolId) return;
    const payload: IUpdateProtocolIntelligenceDetailsPayload = {
      protocolId,
      isActive: protocolDetails?.isActive,
      caseContext,
      relevanceCriteria,
      issueCodes: issueCodeData,
    };
    // Step 1: Call Edit API
    updateProtocolIntelligenceDetails(payload, {
      onSuccess: () => {
        // Step 2: Call Approve API
        approveProtocolIntelligence(
          {protocolId},
          {
            onSuccess: () => {
              setReviewHeaderToast({
                isOpen: true,
                message: t(
                  'ai.review.protocolIntelligence.protocolApprovedToastSuccess'
                ),
                status: ToastType.Success,
              });
              handleApproveArtifacts();
              refetchProtocolData();
            },
            onError: () => {
              setReviewHeaderToast({
                isOpen: true,
                message: t(
                  'ai.review.protocolIntelligence.protocolApprovedToastFailure'
                ),
                status: ToastType.Error,
              });
            },
          }
        );
      },
      onError: (error) => {
        setReviewHeaderToast({
          isOpen: true,
          message:
            error?.response?.data?.detail ??
            t('ai.review.protocolIntelligence.isProtocolUpdateFailure'),
          status: ToastType.Error,
        });
      },
    });
  };

  const mapApiValuesToLocalState = () => {
    if (protocolDetails?.caseContext)
      setCaseContext(protocolDetails.caseContext);
    if (protocolDetails?.relevanceCriteria)
      setRelevanceCriteria(protocolDetails.relevanceCriteria);
    if (protocolDetails?.issueCodes?.length)
      setIssueCodeData(
        protocolDetails?.issueCodes.map((code, index) => ({
          id: index.toString(),
          code: code.code,
          question: code.question,
        }))
      );
  };

  useEffect(() => {
    if (protocolDetails?.protocolId) mapApiValuesToLocalState();
  }, [
    protocolDetails?.caseContext,
    protocolDetails?.relevanceCriteria,
    protocolDetails?.issueCodes,
    protocolDetails?.aIstatus,
    protocolDetails?.protocolId,
  ]);

  const handleDiscardAndLeaveEditProtocolIntelligence = () => {
    mapApiValuesToLocalState();
    showProtocolDetailsPage();
  };

  return (
    <Box
      className="review-approve-drawer"
      data-testid="review-approve-container"
    >
      <ReviewAndApproveHeader
        title={
          showEditProtocolIntelligence
            ? fileName || ''
            : t('ai.review.reviewAndApprove.title')
        }
        approveButtonLabel={
          showEditProtocolIntelligence
            ? t('common.save')
            : t('ai.review.reviewAndApprove.approve')
        }
        handleCancel={
          showEditProtocolIntelligence
            ? handleEditCancelClick
            : handleApproveCancel
        }
        handleApprove={
          showEditProtocolIntelligence ? handleEditSaveClick : handleApprove
        }
      />
      <ReviewLeaveWithoutApprovalModal
        open={showGenerateArtifactsApprovalModal}
        onClose={handleShowGenerateArtifactsApprovalModalClose}
      />
      <ReviewAndApproveContent
        caseContext={caseContext}
        setCaseContext={setCaseContext}
        relevanceCriteria={relevanceCriteria}
        setRelevanceCriteria={setRelevanceCriteria}
        issueCodeData={issueCodeData}
        setIssueCodeData={setIssueCodeData}
      />
      <CancelEditProtocolIntelligenceModal
        showModal={showCancelEditProtocolIntelligenceModal}
        handleCancel={handleDiscardAndLeaveEditProtocolIntelligence}
        handleConfirm={handleEditSaveClick}
      />
    </Box>
  );
};

export default ReviewAndApprove;
